import { AdminPageBarRouteData } from 'src/app/layouts/full/shared/admin-page-bar/admin-page-bar.models';

export interface ResolveAdminBackTargetInput {
  readonly currentUrl: string;
  readonly routeData: AdminPageBarRouteData;
  readonly previousInAppUrl: string | null;
}

/** Normaliza path de navegación (sin query ni hash). */
export function normalizeAdminPath(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) {
    return '/';
  }
  const path = trimmed.split('?')[0]?.split('#')[0] ?? trimmed;
  return path.startsWith('/') ? path : `/${path}`;
}

export function adminPathsEqual(a: string, b: string): boolean {
  return normalizeAdminPath(a) === normalizeAdminPath(b);
}

/** Último crumb con `url` declarado en `urls` (padre semántico de la pantalla). */
export function breadcrumbParentUrl(routeData: AdminPageBarRouteData): string | null {
  const urls = routeData.urls ?? [];
  if (urls.length <= 1) {
    return null;
  }
  for (let index = urls.length - 2; index >= 0; index -= 1) {
    const url = urls[index]?.url?.trim();
    if (url) {
      return url;
    }
  }
  return null;
}

/**
 * Destino de «volver»: historial in-app → padre en urls (si ≠ actual) → backFallback.
 */
export function resolveAdminBackTarget(input: ResolveAdminBackTargetInput): string | null {
  if (input.routeData.backRoot) {
    return null;
  }

  const current = normalizeAdminPath(input.currentUrl);

  const previous = input.previousInAppUrl?.trim();
  if (previous && !adminPathsEqual(previous, current)) {
    return normalizeAdminPath(previous);
  }

  const parentUrl = breadcrumbParentUrl(input.routeData);
  if (parentUrl && !adminPathsEqual(parentUrl, current)) {
    return normalizeAdminPath(parentUrl);
  }

  const fallback = input.routeData.backFallback?.trim();
  if (fallback && !adminPathsEqual(fallback, current)) {
    return normalizeAdminPath(fallback);
  }

  return null;
}

export function canShowAdminBack(input: ResolveAdminBackTargetInput): boolean {
  return resolveAdminBackTarget(input) !== null;
}

/** URL de enlace para un crumb del trail (escritorio); evita auto-referencias. */
export function resolveBreadcrumbCrumbHref(
  crumb: { title: string; url?: string },
  isImmediateParent: boolean,
  input: ResolveAdminBackTargetInput
): string | null {
  const current = normalizeAdminPath(input.currentUrl);
  const declared = crumb.url?.trim();
  if (declared && !adminPathsEqual(declared, current)) {
    return normalizeAdminPath(declared);
  }
  if (isImmediateParent) {
    return resolveAdminBackTarget(input);
  }
  return null;
}

/** Rutas del shell admin (FullComponent); excluye auth/onboard público. */
export function isAdminShellNavigationPath(path: string): boolean {
  const normalized = normalizeAdminPath(path).toLowerCase();
  if (normalized.startsWith('/authentication')) {
    return false;
  }
  if (normalized.startsWith('/onboard')) {
    return false;
  }
  if (normalized.startsWith('/legal')) {
    return false;
  }
  return true;
}
